<script lang="ts">
	/**
	 * `/Operations` — the machinery behind the store (Design §13, §16).
	 *
	 * Nothing on this page blocks checkout, and that is precisely why it needs a page. §6.2's
	 * inline reconcile keeps a purchase succeeding whatever else has stopped, so a store whose
	 * workers died an hour ago looks, from the storefront, exactly like a healthy one — while
	 * renewals stop charging, entitlements stop reaching applications, receipts stop going out and
	 * reconciliation stops noticing any of it. Every panel here is a reading of something that
	 * fails silently.
	 *
	 * Each panel fetches its own data and renders its own loading, empty and error state, so one
	 * endpoint being down costs you that panel rather than the screen. An operator opening this
	 * page during an incident is the worst possible moment to hand back a blank page because an
	 * unrelated query timed out.
	 */

	import { PageHeader } from '../../components';
	import MetricsPanel from './operations/MetricsPanel.svelte';
	import ProviderEventTable from './operations/ProviderEventTable.svelte';
	import ReconciliationPanel from './operations/ReconciliationPanel.svelte';
	import StoreDeliveryTable from './operations/StoreDeliveryTable.svelte';
	import SystemHealth from './operations/SystemHealth.svelte';
</script>

<PageHeader title="Operations">
	<svelte:fragment slot="description">
		Everything the store does when nobody is watching: heartbeats, queues, the provider's events,
		outbound delivery and the reconciliation pass. None of it blocks a purchase, which is why it
		needs a screen of its own.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<p class="mb-2">
			Checkout reconciles inline, so a purchase completes even when every background worker is
			dead. The storefront therefore looks healthy during exactly the failures that cost the most.
		</p>
		<p>
			Read the panels in order: are the workers reporting, are the queues draining, is anything
			failing at the provider or dead on its way out, and is reconciliation finding drift. Money
			is reported per currency throughout and is never added across currencies.
		</p>
	</svelte:fragment>
</PageHeader>

<div class="space-y-6">
	<SystemHealth />
	<MetricsPanel />
	<ProviderEventTable />
	<StoreDeliveryTable />
	<ReconciliationPanel />
</div>
